import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { InvitationCollectionActionTaken } from '@app/v2/event/enums/invitation-collection.enum'
import { InvitationChannelPricingType } from '@app/v2/event/types/invitation-channel-pricing.type'
import { InvitationChannelHistoryState } from '@app/v2/event/types/invitation-tracker.type'
import {
  INVITATION_CHANNEL_BLOCKED_SEND_ICON_SRC,
  INVITATION_CHANNEL_MOBILE_HEADER_ICON_SRC,
  invitationChannelBlockedSendBannerClassLight,
  invitationChannelSendCtaLabel,
  InvitationChannelSendRequest,
  invitationChannelTitle,
  invitationChannelUnitPrice,
  invitationChannelUnitPriceLabel
} from '@app/v2/event/utils/invitation-channel-ui.util'
import { resolveInvitationChannelBlockedSendState } from '@app/v2/event/utils/invitation-channel-blocked-send.util'
import { getActiveInvitationChannelHistory, InvitationChannel } from '@app/v2/event/utils/invitation-channel-status.util'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { InvitationChannelHistoryBodyComponent } from '../invitation-channel-history-body/invitation-channel-history-body.component'

@Component({
  selector: 'app-invitation-channel-mobile-modal',
  standalone: true,
  imports: [NgClass, ModalComponent, InvitationChannelHistoryBodyComponent],
  templateUrl: './invitation-channel-mobile-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitationChannelMobileModalComponent {
  public readonly id = input.required<string>()
  public readonly collectionItemId = input.required<number>()
  public readonly channel = input.required<InvitationChannel>()
  public readonly history = input<InvitationChannelHistoryState | null>(null)
  public readonly pricingKind = input<InvitationChannelPricingType>('invitation')
  public readonly isSending = input(false)
  public readonly actionTaken = input<InvitationCollectionActionTaken | null>(null)
  public readonly inviteeName = input('')
  public readonly channelLatestStatus = input<string | null>(null)
  public readonly isOptOut = input(false)
  public readonly isEventFinished = input(false)

  public readonly sendInvitation = output<InvitationChannelSendRequest>()
  public readonly closed = output<void>()
  public readonly loadMoreHistory = output<void>()

  public readonly activeHistory = computed(() => getActiveInvitationChannelHistory(this.history(), this.collectionItemId(), this.channel()))

  public readonly invitationCount = computed(() => this.activeHistory()?.total ?? 0)

  public readonly headerTitle = computed(() => invitationChannelTitle(this.channel(), this.invitationCount()))

  public readonly headerIcon = computed(() => {
    const src = INVITATION_CHANNEL_MOBILE_HEADER_ICON_SRC[this.channel()]
    return `<img src="${src}" alt="" aria-hidden="true" width="12" height="12" class="tw-h-3 tw-w-3 tw-shrink-0 tw-block" />`
  })

  public readonly unitPriceLabel = computed(() =>
    invitationChannelUnitPriceLabel(
      invitationChannelUnitPrice(this.channel(), this.pricingKind(), this._authService.invitationUnitPriceUsd, this._authService.resendConfirmationUnitPriceUsd)
    )
  )

  public readonly sendCtaLabel = computed(() => (this.isSending() ? 'Sending…' : invitationChannelSendCtaLabel(this.unitPriceLabel())))

  public readonly blockedSendState = computed(() =>
    resolveInvitationChannelBlockedSendState({
      actionTaken: this.actionTaken(),
      channel: this.channel(),
      historyLatestStatus: this.activeHistory()?.entries?.[0]?.status,
      channelLatestStatus: this.channelLatestStatus(),
      inviteeName: this.inviteeName(),
      isOptOut: this.isOptOut(),
      isEventFinished: this.isEventFinished()
    })
  )

  public readonly blockedSendReason = computed(() => this.blockedSendState().reason)

  public readonly showSendCta = computed(() => this.blockedSendState().showSendCta)

  public readonly blockedSendMessage = computed(() => this.blockedSendState().message)

  public readonly blockedSendIconSrc = computed(() => {
    const reason = this.blockedSendReason()
    return reason ? INVITATION_CHANNEL_BLOCKED_SEND_ICON_SRC[reason] : ''
  })

  public readonly blockedSendBannerClass = computed(() => {
    const reason = this.blockedSendReason()
    return reason ? invitationChannelBlockedSendBannerClassLight(reason) : ''
  })

  private readonly _authService = inject(AuthService)

  /**
   * Emits send for the active collection item + channel. Parent closes on success.
   */
  public onSendInvitation(): void {
    if (this.isSending() || !this.showSendCta()) return
    this.sendInvitation.emit({
      collectionItemId: this.collectionItemId(),
      channel: this.channel()
    })
  }

  /**
   * Forwards modal close to the parent.
   */
  public onModalClose(): void {
    this.closed.emit()
  }
}
